import {Injectable} from '@angular/core';
import {Procedure, ProcedureType} from "../models/procedure";
import {Task} from "../models/task";
import {environment} from "@environments/environment";
import {HttpClient} from "@angular/common/http";
import {RestApiManager} from "./rest-api-manager";
import {Step} from "../models/step";
import {Resource, ResourceType} from "../models/resource";
import {ResourceManager} from "./resource-manager";
import {Paginator} from "../models/paginator";
import {Assistance} from "../models/assistance";
import {AccountManagerService} from "./account-manager-service";
import {Utils} from "../utils";

@Injectable({
  providedIn: 'root'
})
export class AuthoringManagerService {

  constructor(
    private accountManager: AccountManagerService,
    private httpClient: HttpClient,
    private restApiManager: RestApiManager,
    private resourceManager: ResourceManager,
  ) { }

  async getProcedures(equipmentId: string, paginator?: Paginator<Procedure>): Promise<Procedure[]> {
    return this.restApiManager.getAll<Procedure>(
      Procedure,
      `${environment.restApi.endpoint}/equipment_models/${equipmentId}/work_instructions`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  async getTasks(procedureId: string, paginator?: Paginator<Task>): Promise<Task[]> {
    return this.restApiManager.getAll<Task>(
      Task,
      `${environment.restApi.endpoint}/work_instructions/${procedureId}/tasks`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  async getSteps(taskId: string, paginator?: Paginator<Step>): Promise<Step[]> {
    return this.restApiManager.getAll<Step>(
      Step,
      `${environment.restApi.endpoint}/tasks/${taskId}/steps`,
      this.accountManager.account.authToken,
      { paginator }
    );
  }

  async getProcedure(equipmentId: string, procedureId: string): Promise<Procedure> {
    return this.restApiManager.get<Procedure>(
      Procedure,
      `${environment.restApi.endpoint}/work_instructions/${procedureId}`,
      this.accountManager.account.authToken
    );
  }

  async createProcedure(procedure: Procedure): Promise<Procedure> {
    return this.restApiManager.post<Procedure>(
      `${environment.restApi.endpoint}/work_instructions`,
      { ...procedure, companyId: this.accountManager.account.company.id },
      this.accountManager.account.authToken
    );
  }

  async updateProcedure(procedureId: string, procedure: Procedure): Promise<Procedure> {
    return this.restApiManager.put<Procedure>(
      `${environment.restApi.endpoint}/work_instructions/${procedureId}`,
      { ...procedure, modelId: procedure.equipmentModel.id, companyId: this.accountManager.account.company.id },
      this.accountManager.account.authToken
    );
  }

  async removeProcedure(procedureId: string): Promise<void> {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/work_instructions/${procedureId}`,
      this.accountManager.account.authToken
    );
  }

  public addTask(task: Task): Promise<Task> {
    return this.restApiManager.post<Task>(
      `${environment.restApi.endpoint}/tasks`,
      task,
      this.accountManager.account.authToken
    );
  }

  public updateTask(procedure: Procedure, taskId: string, task: Task): Promise<Task> {
    return this.restApiManager.put<Task>(
      `${environment.restApi.endpoint}/tasks/${taskId}`,
      { ...task, workInstructionId: procedure.id },
      this.accountManager.account.authToken
    );
  }

  public removeTask(taskId: string) {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/tasks/${taskId}`,
      this.accountManager.account.authToken
    );
  }

  public addStep(step: Step) {
    return this.restApiManager.post<Task>(
      `${environment.restApi.endpoint}/steps`,
      step,
      this.accountManager.account.authToken
    );
  }

  public updateStep(stepId: string, step: Step) {
    return this.restApiManager.put<Task>(
      `${environment.restApi.endpoint}/steps/${stepId}`,
      { ...step, taskId: step.task?.id || step.taskId },
      this.accountManager.account.authToken
    );
  }

  public removeStep(stepId: string) {
    return this.restApiManager.delete(
      `${environment.restApi.endpoint}/steps/${stepId}`,
      this.accountManager.account.authToken
    );
  }

  public async updateStepSequence(taskId: string, steps: Step[]) {
    const positions = {};
    steps.forEach(step => positions[step.id] = step.sequence);
    return this.restApiManager.patch<Step>(
      `${environment.restApi.endpoint}/tasks/${taskId}/steps/positions`,
      { positions },
      this.accountManager.account.authToken
    );
  }

  // TODO
  // Generate a new room in Firebase Realtime Database
  async createRoom(roomName: string): Promise<void> {
    /*await this.angularFireDatabase.database.ref('videocall').child(roomName).update({
      mobile: { operatorReady: false },
      web: { expertReady: false },
      recorder: { recorderReady: false },
    });*/
  }

  // TODO
  onPersonnelReady(roomName: string, callback: () => void) {
    /*this.angularFireDatabase.database.ref('videocall').child(roomName).child('mobile').on('value', value => {
      if (value) {
        if(value.val().operatorReady) {
          callback();
        }
      }
    });*/
  }

  // TODO
  // Check AR device / no AR device
  getDeviceType(roomName: string): Promise<string> {
    return new Promise<string>(() => {});
    /*return from(this.angularFireDatabase.database.ref('videocall').child(roomName).child('mobile').once('value')).pipe(
      map(x => x.val().deviceType),
      take(1),
    ).toPromise();*/
  }

  // TODO
  async setWebReady(roomName: string): Promise<void> {
    /*await this.angularFireDatabase.database.ref('videocall').child(roomName).child('web').update({
      expertReady: true
    });*/
  }

  // TODO
  onRecordingInitializated(roomName: string, callback: () => void) {
    /*this.angularFireDatabase.database.ref('videocall').child(roomName).child('recorder').on('value', value => {
      if(value && value.val().recorderReady) {
        callback();
      }
    });*/
  }

  // TODO
  onLiveAssistanceVideoRef(equipmentId: string, rooomName: string, callback: (url) => void) {
    /*const mediaPathRef = 'companies/' + this.accountManager.companyId + '/equipments/' + equipmentId + '/medias';
    this.db.collection(mediaPathRef).doc(rooomName).snapshotChanges().pipe(
      skipWhile(x => !x),
      map(doc => doc.payload.data()['storage_ref']),
      take(1),
      tap(async storageNameRef => {
        callback(mediaPathRef + '/' + storageNameRef);
      }),
    );*/
  }

  // Bind resource to procedure as main resource
  bindResourceToProcedure(procedure: Procedure, resource: Resource) {
    return this.restApiManager.patch<Object>(
      `${environment.restApi.endpoint}/work_instructions/${procedure.id}/resources`,
      { resourceId: resource.id, isMain: true },
      this.accountManager.account.authToken
    );
  }

  // TODO wait API fix (#188)
  unbindResourceFromProcedure(procedure: Procedure, resource: Resource) {
    return this.restApiManager.patch<Object>(
      `${environment.restApi.endpoint}/work_instructions/${procedure.id}/resources/${resource.id}`,
      { resourceId: resource.id, isMain: true },
      this.accountManager.account.authToken
    );
  }

  public async bindResourceToMaintenance(assistance: Assistance, resource: Resource) {
    // TODO change assistance.maintenanceId to assistance.id
    return this.restApiManager.post<Object>(
      `${environment.restApi.endpoint}/maintenances/${assistance.maintenance.id}/resource`,
      { resourceId: resource.id },
      this.accountManager.account.authToken
    );
  }

  async uploadVideocallContent(assistance: Assistance, resourceType: ResourceType, file: File) {

    // Upload resource as hidden
    const resource = await this.resourceManager.uploadResource(assistance.equipment ? assistance.equipment.model : assistance.maintenance.equipment.model, file, [], null, true);

    // Bind resource to maintenance
    await this.bindResourceToMaintenance(assistance, resource);

    // Get download url
    return (await this.resourceManager.getDownloadSignedUrl(resource)).url;

  }

  async exportProcedureToExternalService(companyId, equipmentId, procedureId) {
    await this.httpClient.post(`${environment.apiEndpoint}/exportProcedure`, {
      companyId, equipmentId, procedureId
    }).toPromise();
  }

  public async getMainResource(procedure: Procedure): Promise<Resource> {
    return await this.restApiManager.get<Resource>(
      Resource,
      `${environment.restApi.endpoint}/work_instructions/${procedure.id}/resources/main`,
      this.accountManager.account.authToken
    );
  }

  async inferProcedureTypes(procedure: Procedure): Promise<ProcedureType[]> {

    const types = [ ProcedureType.TEXT ];

    // If contains resources is an AR procedure
    const inferARType = async () => {
      const tasks = await this.getTasks(procedure.id, new Paginator<Task>({ignorePagination: true}))
      const steps = Utils.arrayFlat(await Promise.all(tasks.map(task => this.getSteps(task.id, new Paginator<Step>({ignorePagination: true})))));
      const resources = Utils.arrayFlat(await Promise.all(steps.map(step => this.resourceManager.getStepResources(step.id, new Paginator<Resource>({ignorePagination: true})))));
      if (resources.length > 0) {
        types.push(ProcedureType.MIXED_REALITY);
      }
    }

    // If contain main video resource is a video procedure
    const inferVideoType = async () => {
      const media = await this.getMainResource(procedure).catch(_ => {});
      if (media) {
        types.push(ProcedureType.VIDEO);
      }
    }

    await Promise.all([
      inferARType(),
      inferVideoType()
    ]);

    return types;

  }

}
